import { Location } from 'history';
import { BlogPostFormData } from 'mk2/apps/blogs/components/BlogPostCreateForm';
import { AvatarGridType } from 'mk2/apps/blogs/components/MoreAboutMe';
import { ProfileDetailDataNormalized } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetail.reducers';
import { ScrollToPosition } from 'mk2/apps/blogs/containers/ProfileDetail/ProfileDetailPage';
import { BlogPostType } from 'mk2/apps/blogs/schemas';
import { PageRef } from 'mk2/apps/forum/schemas';
import { APIFailureAction, APIRequestAction, APISuccessAction } from 'mk2/helpers/action';
import { NormalizedResponse } from 'mk2/helpers/api';
import { Action } from 'redux';

///////////////////////////////////////////////////////////////////
// Fetch profile detail

export const PROFILE_DETAIL_FETCH_TRIGGER = 'PROFILE_DETAIL_FETCH_TRIGGER';
export interface ProfileDetailFetchTriggerAction {
    readonly type: typeof PROFILE_DETAIL_FETCH_TRIGGER;
    readonly username: string;
}
export const PROFILE_DETAIL_FETCH_REQUEST = 'PROFILE_DETAIL_FETCH_REQUEST';
export interface ProfileDetailFetchRequestAction extends APIRequestAction {
    readonly type: typeof PROFILE_DETAIL_FETCH_REQUEST;
    readonly username: string;
}
export interface ProfileDetailFetchNormalizedResponse extends NormalizedResponse {
    readonly result: ProfileDetailDataNormalized;
}
export const PROFILE_DETAIL_FETCH_SUCCESS = 'PROFILE_DETAIL_FETCH_SUCCESS';
export interface ProfileDetailFetchSuccessAction extends APISuccessAction<ProfileDetailFetchNormalizedResponse> {
    readonly type: typeof PROFILE_DETAIL_FETCH_SUCCESS;
    readonly username: string;
}
export const PROFILE_DETAIL_FETCH_FAILURE = 'PROFILE_DETAIL_FETCH_FAILURE';
export interface ProfileDetailFetchFailureAction extends APIFailureAction {
    readonly type: typeof PROFILE_DETAIL_FETCH_FAILURE;
    readonly username: string;
}

export const profileDetailFetchTrigger =
    (username: string): ProfileDetailFetchTriggerAction => ({
        type: PROFILE_DETAIL_FETCH_TRIGGER, username,
    });
export const profileDetailFetchApi = {
    request: (username: string): ProfileDetailFetchRequestAction => ({
        type: PROFILE_DETAIL_FETCH_REQUEST, username,
    }),
    success: (username: string, response: ProfileDetailFetchNormalizedResponse): ProfileDetailFetchSuccessAction => ({
        type: PROFILE_DETAIL_FETCH_SUCCESS, username, response,
    }),
    failure: (username: string, error: any): ProfileDetailFetchFailureAction => ({
        type: PROFILE_DETAIL_FETCH_FAILURE, username, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Fetch profile feed

export interface ProfileFeedFetchAction extends Action {
    readonly username: string;
    readonly pageRef: PageRef;
    readonly postType: BlogPostType;
    readonly query: string;
}

export const PROFILE_FEED_FETCH_TRIGGER = 'PROFILE_FEED_FETCH_TRIGGER';
export interface ProfileFeedFetchTriggerAction extends ProfileFeedFetchAction {
    readonly type: typeof PROFILE_FEED_FETCH_TRIGGER;
    readonly currentUrl: Location;
    readonly numPostsBeforeLoad: number;
}
export const PROFILE_FEED_FETCH_REQUEST = 'PROFILE_FEED_FETCH_REQUEST';
export interface ProfileFeedFetchRequestAction extends ProfileFeedFetchAction, APIRequestAction {
    readonly type: typeof PROFILE_FEED_FETCH_REQUEST;
}
export interface ProfileFeedFetchNormalizedResponse extends NormalizedResponse {
    readonly result: {
        complaints: number[];
        displayVisibilityAlert: boolean;
        numberOfPages: number;
        posts: number[];
        userStats: {
            blogsAlbumsCount: number;
            blogsArticlesCount: number;
            blogsArticleDraftsCount: number;
            blogsShortMessagesCount: number;
        }
    };
}
export const PROFILE_FEED_FETCH_SUCCESS = 'PROFILE_FEED_FETCH_SUCCESS';
export interface ProfileFeedFetchSuccessAction extends ProfileFeedFetchAction, APISuccessAction<ProfileFeedFetchNormalizedResponse> {
    readonly type: typeof PROFILE_FEED_FETCH_SUCCESS;
    readonly page: number;
    readonly hasComplaints: boolean;
}
export const PROFILE_FEED_FETCH_FAILURE = 'PROFILE_FEED_FETCH_FAILURE';
export interface ProfileFeedFetchFailureAction extends ProfileFeedFetchAction, APIFailureAction {
    readonly type: typeof PROFILE_FEED_FETCH_FAILURE;
}

export const profileFeedFetchTrigger =
    (username: string, pageRef: PageRef, postType: BlogPostType, query: string, currentUrl: Location, numPostsBeforeLoad: number = 0): ProfileFeedFetchTriggerAction => ({
        type: PROFILE_FEED_FETCH_TRIGGER, username, pageRef, postType, query, currentUrl, numPostsBeforeLoad,
    });
export const profileFeedFetchApi = {
    request: (username: string, pageRef: PageRef, postType: BlogPostType, query: string): ProfileFeedFetchRequestAction => ({
        type: PROFILE_FEED_FETCH_REQUEST, username, pageRef, postType, query,
    }),
    success: (
        username: string, pageRef: PageRef, postType: BlogPostType, query: string, page: number,
        hasComplaints: boolean, response: ProfileFeedFetchNormalizedResponse,
    ): ProfileFeedFetchSuccessAction => ({
        type: PROFILE_FEED_FETCH_SUCCESS, username, pageRef, postType, query, page, hasComplaints, response,
    }),
    failure: (username: string, pageRef: PageRef, postType: BlogPostType, query: string, error: any): ProfileFeedFetchFailureAction => ({
        type: PROFILE_FEED_FETCH_FAILURE, username, pageRef, postType, query, error,
    }),
};

/////////////////////////////////////////////////////////////////////
// Profile feed - scrollto

export const PROFILE_FEED_SCROLLTO_TRIGGER = 'PROFILE_FEED_SCROLLTO_TRIGGER';
export interface ProfileFeedScrollToTriggerAction {
    readonly type: typeof PROFILE_FEED_SCROLLTO_TRIGGER;
    readonly scrollto: ScrollToPosition;
}

export const profileFeedScrollToTrigger = (scrollto: ScrollToPosition): ProfileFeedScrollToTriggerAction => ({
    type: PROFILE_FEED_SCROLLTO_TRIGGER, scrollto,
});

/////////////////////////////////////////////////////////////////////
// Profile: delete avatar (by admin)

export const PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER = 'PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER';
export interface ProfileDeleteAvatarByAdminTriggerAction {
    readonly type: typeof PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER;
    readonly username: string;
}
export const PROFILE_DELETE_AVATAR_BY_ADMIN_REQUEST = 'PROFILE_DELETE_AVATAR_BY_ADMIN_REQUEST';
export interface ProfileDeleteAvatarByAdminRequestAction extends APIRequestAction {
    readonly type: typeof PROFILE_DELETE_AVATAR_BY_ADMIN_REQUEST;
    readonly username: string;
}
export interface ProfileDeleteAvatarByAdminNormalizedResponse extends NormalizedResponse {
    readonly result: { profile: number };
}
export const PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS = 'PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS';
export interface ProfileDeleteAvatarByAdminSuccessAction extends APISuccessAction<ProfileDeleteAvatarByAdminNormalizedResponse> {
    readonly type: typeof PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS;
    readonly username: string;
}
export const PROFILE_DELETE_AVATAR_BY_ADMIN_FAILURE = 'PROFILE_DELETE_AVATAR_BY_ADMIN_FAILURE';
export interface ProfileDeleteAvatarByAdminFailureAction extends APIFailureAction {
    readonly type: typeof PROFILE_DELETE_AVATAR_BY_ADMIN_FAILURE;
    readonly username: string;
}

export const profileDeleteAvatarByAdminTrigger = (username: string): ProfileDeleteAvatarByAdminTriggerAction => ({
    type: PROFILE_DELETE_AVATAR_BY_ADMIN_TRIGGER, username,
});
export const profileDeleteAvatarByAdminApi = {
    request: (username: string): ProfileDeleteAvatarByAdminRequestAction => ({
        type: PROFILE_DELETE_AVATAR_BY_ADMIN_REQUEST, username,
    }),
    success: (username: string, response: ProfileDeleteAvatarByAdminNormalizedResponse): ProfileDeleteAvatarByAdminSuccessAction => ({
        type: PROFILE_DELETE_AVATAR_BY_ADMIN_SUCCESS, username, response,
    }),
    failure: (username: string, error: any): ProfileDeleteAvatarByAdminFailureAction => ({
        type: PROFILE_DELETE_AVATAR_BY_ADMIN_FAILURE, username, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Profile: like/unlike user

export interface ProfileSetLikeUserAction extends Action {
    readonly username: string;
    readonly like: boolean;
}

export const PROFILE_SET_LIKE_USER_TRIGGER = 'PROFILE_SET_LIKE_USER_TRIGGER';
export interface ProfileSetLikeUserTriggerAction extends ProfileSetLikeUserAction {
    readonly type: typeof PROFILE_SET_LIKE_USER_TRIGGER;
}
export const PROFILE_SET_LIKE_USER_REQUEST = 'PROFILE_SET_LIKE_USER_REQUEST';
export interface ProfileSetLikeUserRequestAction extends ProfileSetLikeUserAction, APIRequestAction {
    readonly type: typeof PROFILE_SET_LIKE_USER_REQUEST;
}
export const PROFILE_SET_LIKE_USER_SUCCESS = 'PROFILE_SET_LIKE_USER_SUCCESS';
export interface ProfileSetLikeUserSuccessAction extends ProfileSetLikeUserAction, APISuccessAction {
    readonly type: typeof PROFILE_SET_LIKE_USER_SUCCESS;
    readonly response: any;
}
export const PROFILE_SET_LIKE_USER_FAILURE = 'PROFILE_SET_LIKE_USER_FAILURE';
export interface ProfileSetLikeUserFailureAction extends ProfileSetLikeUserAction, APIFailureAction {
    readonly type: typeof PROFILE_SET_LIKE_USER_FAILURE;
}

export const profileSetLikeUserTrigger =
    (username: string, like: boolean): ProfileSetLikeUserTriggerAction => ({
        type: PROFILE_SET_LIKE_USER_TRIGGER, username, like,
    });
export const profileSetLikeUserApi = {
    request: (username: string, like: boolean): ProfileSetLikeUserRequestAction => ({
        type: PROFILE_SET_LIKE_USER_REQUEST, username, like,
    }),
    success: (username: string, like: boolean, response: any): ProfileSetLikeUserSuccessAction => ({
        type: PROFILE_SET_LIKE_USER_SUCCESS, username, like, response,
    }),
    failure: (username: string, like: boolean, error: any): ProfileSetLikeUserFailureAction => ({
        type: PROFILE_SET_LIKE_USER_FAILURE, username, like, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Profile: expand avatar grid and fetch more users

export interface ProfileExpandAvatarGridAction extends Action {
    readonly avatarGridType: AvatarGridType;
    readonly username: string;
}

export const PROFILE_EXPAND_AVATAR_GRID_TRIGGER = 'PROFILE_EXPAND_AVATAR_GRID_TRIGGER';
export interface ProfileExpandAvatarGridTriggerAction extends ProfileExpandAvatarGridAction {
    readonly type: typeof PROFILE_EXPAND_AVATAR_GRID_TRIGGER;
}
export const PROFILE_EXPAND_AVATAR_GRID_REQUEST = 'PROFILE_EXPAND_AVATAR_GRID_REQUEST';
export interface ProfileExpandAvatarGridRequestAction extends ProfileExpandAvatarGridAction, APIRequestAction {
    readonly type: typeof PROFILE_EXPAND_AVATAR_GRID_REQUEST;
}
export interface ProfileExpandAvatarGridNormalizedResponse extends NormalizedResponse {
    readonly result: {
        hasMore: boolean;
        users: number[];
    };
}
export const PROFILE_EXPAND_AVATAR_GRID_SUCCESS = 'PROFILE_EXPAND_AVATAR_GRID_SUCCESS';
export interface ProfileExpandAvatarGridSuccessAction extends ProfileExpandAvatarGridAction, APISuccessAction {
    readonly type: typeof PROFILE_EXPAND_AVATAR_GRID_SUCCESS;
    readonly response: ProfileExpandAvatarGridNormalizedResponse;
}
export const PROFILE_EXPAND_AVATAR_GRID_FAILURE = 'PROFILE_EXPAND_AVATAR_GRID_FAILURE';
export interface ProfileExpandAvatarGridFailureAction extends ProfileExpandAvatarGridAction, APIFailureAction {
    readonly type: typeof PROFILE_EXPAND_AVATAR_GRID_FAILURE;
}

export const profileExpandAvatarGridTrigger =
    (avatarGridType: AvatarGridType, username: string): ProfileExpandAvatarGridTriggerAction => ({
        type: PROFILE_EXPAND_AVATAR_GRID_TRIGGER, avatarGridType, username,
    });
export const profileExpandAvatarGridApi = {
    request: (avatarGridType: AvatarGridType, username: string): ProfileExpandAvatarGridRequestAction => ({
        type: PROFILE_EXPAND_AVATAR_GRID_REQUEST, avatarGridType, username,
    }),
    success: (avatarGridType: AvatarGridType, username: string, response: any): ProfileExpandAvatarGridSuccessAction => ({
        type: PROFILE_EXPAND_AVATAR_GRID_SUCCESS, avatarGridType, username, response,
    }),
    failure: (avatarGridType: AvatarGridType, username: string, error: any): ProfileExpandAvatarGridFailureAction => ({
        type: PROFILE_EXPAND_AVATAR_GRID_FAILURE, avatarGridType, username, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Profile: expand group list (load all user's subscribed groups)

export interface ProfileExpandGroupListAction extends Action {
    readonly username: string;
}

export const PROFILE_EXPAND_GROUP_LIST_TRIGGER = 'PROFILE_EXPAND_GROUP_LIST_TRIGGER';
export interface ProfileExpandGroupListTriggerAction extends ProfileExpandGroupListAction {
    readonly type: typeof PROFILE_EXPAND_GROUP_LIST_TRIGGER;
}
export const PROFILE_EXPAND_GROUP_LIST_REQUEST = 'PROFILE_EXPAND_GROUP_LIST_REQUEST';
export interface ProfileExpandGroupListRequestAction extends ProfileExpandGroupListAction, APIRequestAction {
    readonly type: typeof PROFILE_EXPAND_GROUP_LIST_REQUEST;
}
export interface ProfileExpandGroupListNormalizedResponse extends NormalizedResponse {
    readonly result: {
        hasMore: boolean;
        groups: number[];
    };
}
export const PROFILE_EXPAND_GROUP_LIST_SUCCESS = 'PROFILE_EXPAND_GROUP_LIST_SUCCESS';
export interface ProfileExpandGroupListSuccessAction extends ProfileExpandGroupListAction, APISuccessAction {
    readonly type: typeof PROFILE_EXPAND_GROUP_LIST_SUCCESS;
    readonly response: ProfileExpandGroupListNormalizedResponse;
}
export const PROFILE_EXPAND_GROUP_LIST_FAILURE = 'PROFILE_EXPAND_GROUP_LIST_FAILURE';
export interface ProfileExpandGroupListFailureAction extends ProfileExpandGroupListAction, APIFailureAction {
    readonly type: typeof PROFILE_EXPAND_GROUP_LIST_FAILURE;
}

export const profileExpandGroupListTrigger =
    (username: string): ProfileExpandGroupListTriggerAction => ({
        type: PROFILE_EXPAND_GROUP_LIST_TRIGGER, username,
    });
export const profileExpandGroupListApi = {
    request: (username: string): ProfileExpandGroupListRequestAction => ({
        type: PROFILE_EXPAND_GROUP_LIST_REQUEST, username,
    }),
    success: (username: string, response: any): ProfileExpandGroupListSuccessAction => ({
        type: PROFILE_EXPAND_GROUP_LIST_SUCCESS, username, response,
    }),
    failure: (username: string, error: any): ProfileExpandGroupListFailureAction => ({
        type: PROFILE_EXPAND_GROUP_LIST_FAILURE, username, error,
    }),
};

///////////////////////////////////////////////////////////////////
// Blog Post Create

export const BLOG_POST_CREATE_TRIGGER = 'BLOG_POST_CREATE_TRIGGER';
export interface BlogPostCreateTriggerAction {
    readonly type: typeof BLOG_POST_CREATE_TRIGGER;
    readonly username: string;
    readonly postFd: BlogPostFormData;
}
export const BLOG_POST_CREATE_REQUEST = 'BLOG_POST_CREATE_REQUEST';
export interface BlogPostCreateRequestAction extends APIRequestAction {
    readonly type: typeof BLOG_POST_CREATE_REQUEST;
    readonly username: string;
    readonly postFd: BlogPostFormData;
}
export interface BlogPostCreateNormalizedResponse extends NormalizedResponse {
    readonly result: { post: number; };
}
export const BLOG_POST_CREATE_SUCCESS = 'BLOG_POST_CREATE_SUCCESS';
export interface BlogPostCreateSuccessAction extends APISuccessAction<BlogPostCreateNormalizedResponse> {
    readonly type: typeof BLOG_POST_CREATE_SUCCESS;
    readonly username: string;
    readonly postFd: BlogPostFormData;
}
export const BLOG_POST_CREATE_FAILURE = 'BLOG_POST_CREATE_FAILURE';
export interface BlogPostCreateFailureAction extends APIFailureAction {
    readonly type: typeof BLOG_POST_CREATE_FAILURE;
    readonly username: string;
    readonly postFd: BlogPostFormData;
}

export const blogPostCreateTrigger =
    (username: string, postFd: BlogPostFormData): BlogPostCreateTriggerAction => ({
        type: BLOG_POST_CREATE_TRIGGER, username, postFd,
    });
export const blogPostCreateApi = {
    request: (username: string, postFd: BlogPostFormData): BlogPostCreateRequestAction => ({
        type: BLOG_POST_CREATE_REQUEST, username, postFd,
    }),
    success: (username: string, postFd: BlogPostFormData, response: BlogPostCreateNormalizedResponse): BlogPostCreateSuccessAction => ({
        type: BLOG_POST_CREATE_SUCCESS, username, postFd, response,
    }),
    failure: (username: string, postFd: BlogPostFormData, error: any): BlogPostCreateFailureAction => ({
        type: BLOG_POST_CREATE_FAILURE, username, postFd, error,
    }),
};

